import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  ArrowBackRounded,
  FactCheckRounded,
  BlockRounded,
  CalendarMonthRounded,
  CheckCircleRounded,
  CheckRounded,
  ContentCopyRounded,
  DeleteForeverRounded,
  EditRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  FolderSpecialRounded,
  MenuBookRounded,
  PeopleOutlineRounded,
  PersonAddAlt1Rounded,
  PersonRemoveRounded,
  SwapHorizRounded,
  PlayArrowRounded,
  WarningAmberRounded,
  TuneRounded,
} from '@mui/icons-material';
import {
  collection,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isModuleEnabled, sectionsTotalCapacity } from '@dosfilos/domain';
import { useTenantConfig } from '../../hooks/use-tenant-config';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { useTenantStore, useTenantTimeZone } from '../../stores/tenant.store';
import { todayInZoneWeb } from '@dosfilos/shared';
import { errorText } from '../../lib/error-text';
import { enrollmentApi } from '../../lib/api/enrollment.api';
import { sectionStarted } from '../../lib/section-start';
import { useScheduleBlockText, type ScheduleBlock } from '../portal-v2/components/ScheduleText';
import { rosterRowActions, sectionRowActions } from './term-detail.actions';
import { termHeaderActions } from './academic-structure.actions';
import { useCanManageAcademics } from '../../hooks/use-can-manage-academics';
import { useCurrentMembership } from '../../hooks/use-current-membership';
import { useCurrentPersonId } from '../../hooks/use-current-person-id';
import { useAcademic } from '../../hooks/use-academic';
import { useEnrollment } from '../../hooks/use-enrollment';
import { EditSectionDialog } from './components/EditSectionDialog';
import { EnrollStudentDialog } from './components/EnrollStudentDialog';
import { EditTermDialog } from './components/EditTermDialog';
import { academicApi } from '../../lib/api/academic.api';
import { ResyncSyllabusButton } from './components/ResyncSyllabusButton';
import { countsAsEnrolled, definedCapacity } from '../../lib/enrollment-count';
import { formatDate } from '../../lib/i18n-format';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const FACULTY_ROLES = [
  'faculty',
  'docente',
  'tutor',
  'decano',
  'academico',
  'coordinador',
  'capellan',
];

function fmtDate(val: any): string {
  if (!val) return '';
  let d: Date;
  if (val.toDate) {
    // Firestore Timestamp
    d = val.toDate();
    // Legacy data was stored as new Date('YYYY-MM-DD') which is UTC midnight.
    // In negative-offset timezones (Chile UTC-3) that displays as the previous day.
    // Detect UTC-midnight pattern and extract the UTC date components instead.
    if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      // Re-create as local noon so toLocaleDateString shows the correct day
      d = new Date(`${y}-${m}-${day}T12:00:00`);
    }
  } else if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    // Pure date string (YYYY-MM-DD): append midday to avoid UTC-midnight off-by-one
    d = new Date(val + 'T12:00:00');
  } else {
    d = new Date(val);
  }
  if (isNaN(d.getTime())) return '';
  return formatDate(d, { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusColor(status: string): 'success' | 'error' | 'warning' | 'primary' | 'default' {
  if (status === 'open') return 'success';
  if (status === 'in_progress') return 'primary';
  if (status === 'cancelled') return 'error';
  if (status === 'planned') return 'warning';
  return 'default';
}

/** Estados de período y de sección, traducidos. Uno desconocido se muestra
 *  tal cual antes que inventarle un nombre. */
const KNOWN_STATUSES = ['open', 'in_progress', 'closed', 'planned', 'cancelled', 'grades_closed'];
function statusLabel(status: string, t: TFunction): string {
  return KNOWN_STATUSES.includes(status) ? t(`sections.statusLabels.${status}`) : status;
}

// ─── StudentStatusRow ─────────────────────────────────────────────────────────

interface StudentStatusRowProps {
  person: DocumentData;
  enrolledSectionCount: number;
}

function StudentStatusRow({ person, enrolledSectionCount }: StudentStatusRowProps) {
  const { t } = useTranslation('sis');
  const isEnrolled = enrolledSectionCount > 0;
  return (
    <TableRow
      sx={{
        bgcolor: isEnrolled ? 'rgba(34,197,94,0.04)' : 'rgba(251,191,36,0.04)',
        '&:hover': { bgcolor: isEnrolled ? 'rgba(34,197,94,0.08)' : 'rgba(251,191,36,0.08)' },
      }}
    >
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {isEnrolled ? (
            <CheckCircleRounded sx={{ fontSize: 16, color: 'success.light', flexShrink: 0 }} />
          ) : (
            <WarningAmberRounded sx={{ fontSize: 16, color: 'warning.light', flexShrink: 0 }} />
          )}
          <Box>
            <Typography variant="body2" fontWeight={600} fontSize={13}>
              {person.firstName} {person.lastName}
            </Typography>
            {person.email && (
              <Typography variant="caption" color="text.disabled">
                {person.email}
              </Typography>
            )}
          </Box>
        </Stack>
      </TableCell>
      <TableCell>
        {isEnrolled ? (
          <Chip
            label={t('sections.termDetail.studentRow.subjects', { count: enrolledSectionCount })}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: 11, height: 22 }}
          />
        ) : (
          <Chip
            label={t('sections.termDetail.studentRow.notEnrolled')}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontSize: 11, height: 22 }}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

// ─── ProgramPanel ─────────────────────────────────────────────────────────────

interface ProgramPanelProps {
  program: { id: string; name: string };
  sections: DocumentData[]; // sections of THIS term belonging to THIS program
  allSections: DocumentData[]; // all sections of this term (for enrollment cross-ref)
  courses: DocumentData[];
  people: DocumentData[];
  admissions: DocumentData[]; // accepted admissions
  /** Las inscripciones en vivo: SÓLO para gestión (admitidos → inscritos). Un
   *  docente o un mentor recibe `[]` y sus cifras salen del servidor. */
  enrollments: DocumentData[];
  /** Inscritos de una sección. La página decide de dónde: en vivo para
   *  gestión, del resumen del servidor para los demás (PR 3A). */
  enrolledCountOf: (sectionId: string) => number;
  /** «Nombre Apellido» del titular, o '' si no se sabe. */
  facultyNameOf: (facultyId: string | undefined) => string;
  /** El inicio del período: una sección sin fecha propia toma este (H8). */
  termStart?: unknown;
  onEdit: (s: DocumentData) => void;
  /** Quién puede cambiar la sección. Un docente llega a esta pantalla para VER
   *  sus secciones; cambiar el titular, el programa o el estado es trabajo de
   *  registro. El servidor ya lo exige — mostrar el botón igual sería ofrecer
   *  una acción que falla. */
  canEdit: boolean;
  onDelete: (s: DocumentData) => void;
  onHardDelete: (s: DocumentData) => void;
  onReactivate: (s: DocumentData) => void;
  onEnroll: (s: DocumentData) => void;
}

function ProgramPanel({
  program,
  sections,
  allSections,
  courses,
  people,
  admissions,
  enrollments,
  enrolledCountOf,
  facultyNameOf,
  termStart,
  onEdit,
  canEdit,
  onDelete,
  onHardDelete,
  onReactivate,
  onEnroll,
}: ProgramPanelProps) {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const { dropEnrollment, isDroppingEnrollment } = useEnrollment();
  const blockText = useScheduleBlockText();
  const todayYmd = todayInZoneWeb(useTenantTimeZone());
  // El ACTA (H1): la abren Registro y administración en cualquier sección, y el
  // profesor en las suyas. El servidor lo exige igual; acá solo se ofrece.
  const membership = useCurrentMembership();
  const myPersonId = useCurrentPersonId();
  const isRegistry =
    !!membership?.roles?.includes('tenantAdmin') || !!membership?.roles?.includes('registrar');
  const canOpenActa = (s: DocumentData) =>
    s.status !== 'cancelled' &&
    (isRegistry ||
      (!!myPersonId &&
        (s.facultyId === myPersonId ||
          ((s.assistantIds as string[] | undefined) ?? []).includes(myPersonId))));
  // LA NÓMINA trae nombre y correo de los alumnos: el servidor se la da a
  // gestión y a quien enseña en ESA sección (`listSectionRoster`). A los demás
  // —un mentor, el docente del curso de al lado— no se les ofrece el botón.
  const canOpenRoster = (s: DocumentData) =>
    canEdit ||
    (!!myPersonId &&
      (s.facultyId === myPersonId ||
        ((s.assistantIds as string[] | undefined) ?? []).includes(myPersonId)));
  const [open, setOpen] = useState(false);
  const [showStudents, setShowStudents] = useState(false);
  const [rosterSection, setRosterSection] = useState<DocumentData | null>(null);
  const queryClient = useQueryClient();
  // Del servidor (PR 3A), y se vuelve a pedir tras dar de baja o mover: antes
  // se armaba con las inscripciones y fichas en vivo del navegador.
  const rosterQ = useQuery({
    queryKey: ['section-roster', currentTenantId, rosterSection?.id],
    enabled: !!currentTenantId && !!rosterSection,
    queryFn: () =>
      academicApi.listSectionRoster({
        tenantId: currentTenantId!,
        sectionId: rosterSection!.id as string,
      }),
  });
  const refreshRoster = () => void queryClient.invalidateQueries({ queryKey: ['section-roster'] });
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  // H8: la baja deja W si la sección ya empezó; si no, no deja rastro. Se
  // anticipa en el diálogo; decide el servidor.
  const [dropTarget, setDropTarget] = useState<{
    enrollmentId: string;
    name: string;
    started: boolean;
  } | null>(null);
  const [dropReason, setDropReason] = useState('');
  const [dropError, setDropError] = useState<string | null>(null);
  const [dropNotice, setDropNotice] = useState<string | null>(null);
  const financeOn = isModuleEnabled(useTenantConfig().modules, 'finance');
  // H8: cambio de sección dentro del mismo curso y período.
  const [moveTarget, setMoveTarget] = useState<{
    enrollmentId: string;
    name: string;
    options: DocumentData[];
  } | null>(null);
  const [moveTo, setMoveTo] = useState('');
  const [moveError, setMoveError] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);

  const copyRoster = (text: string, key: string) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    });
  };

  const confirmDrop = async () => {
    if (!currentTenantId || !dropTarget) return;
    setDropError(null);
    try {
      const result = await dropEnrollment({
        tenantId: currentTenantId,
        enrollmentId: dropTarget.enrollmentId,
        ...(dropReason.trim() ? { reason: dropReason.trim() } : {}),
      });
      setDropTarget(null);
      setDropReason('');
      refreshRoster();
      // H8: con una W el cobro no se recalcula; si corresponde una devolución,
      // la decide Finanzas.
      if (result?.billing === 'keep' && financeOn)
        setDropNotice(t('sections.roster.wBillingNotice'));
    } catch (err) {
      setDropError(errorText(err));
    }
  };

  const confirmMove = async () => {
    if (!currentTenantId || !moveTarget || !moveTo) return;
    setMoveError(null);
    setMoving(true);
    try {
      await enrollmentApi.changeEnrollmentSection({
        tenantId: currentTenantId,
        enrollmentId: moveTarget.enrollmentId,
        toSectionId: moveTo,
      });
      setMoveTarget(null);
      setMoveTo('');
      refreshRoster();
    } catch (err) {
      setMoveError(errorText(err));
    } finally {
      setMoving(false);
    }
  };

  const [sortKey, setSortKey] = useState<
    'course' | 'startDate' | 'faculty' | 'enrollment' | 'status'
  >('startDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleRequestSort = (property: typeof sortKey) => {
    const isAsc = sortKey === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortKey(property);
  };

  const sortedSections = useMemo(() => {
    const arr = [...sections];
    arr.sort((a, b) => {
      let aVal: any = 0;
      let bVal: any = 0;

      switch (sortKey) {
        case 'course': {
          const cA = courses.find((c) => c.id === a.courseId);
          const cB = courses.find((c) => c.id === b.courseId);
          aVal = (cA?.name || a.courseId || '').toLowerCase();
          bVal = (cB?.name || b.courseId || '').toLowerCase();
          break;
        }
        case 'startDate': {
          // If a date is missing, treat it as Infinity so it appears at the end
          aVal = a.startDate
            ? a.startDate.toDate
              ? a.startDate.toDate().getTime()
              : new Date(a.startDate).getTime()
            : Infinity;
          bVal = b.startDate
            ? b.startDate.toDate
              ? b.startDate.toDate().getTime()
              : new Date(b.startDate).getTime()
            : Infinity;
          break;
        }
        case 'faculty': {
          aVal = facultyNameOf(a.facultyId).toLowerCase();
          bVal = facultyNameOf(b.facultyId).toLowerCase();
          break;
        }
        case 'enrollment': {
          aVal = enrolledCountOf(a.id);
          bVal = enrolledCountOf(b.id);
          break;
        }
        case 'status': {
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        }
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [sections, sortKey, sortDirection, courses, facultyNameOf, enrolledCountOf]);

  // Students admitted to this program
  const admittedStudentIds = useMemo(
    () =>
      new Set(
        admissions
          .filter((a) => a.programId === program.id && a.status === 'accepted')
          .map((a) => a.personId ?? a.studentId)
          .filter(Boolean),
      ),
    [admissions, program.id],
  );

  // Sección IDs of this program in this term
  const programSectionIds = useMemo(() => new Set(sections.map((s) => s.id)), [sections]);

  // Per admitted student: how many sections of THIS program are they enrolled in
  const studentEnrollMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const studentId of admittedStudentIds) {
      const count = enrollments.filter(
        (e) =>
          e.studentId === studentId && programSectionIds.has(e.sectionId) && countsAsEnrolled(e),
      ).length;
      map.set(studentId as string, count);
    }
    return map;
  }, [admittedStudentIds, enrollments, programSectionIds]);

  const admittedCount = admittedStudentIds.size;
  const enrolledCount = Array.from(studentEnrollMap.values()).filter((n) => n > 0).length;
  const conversionPct = admittedCount > 0 ? Math.round((enrolledCount / admittedCount) * 100) : 0;

  const activeSections = sections.filter((s) => s.status !== 'cancelled');
  // null = sin límite: basta UNA sección sin cupo para que el total no lo tenga.
  const totalCapacity = sectionsTotalCapacity(activeSections.map((s) => s.maxEnrollment));
  const activeSectionIds = new Set(activeSections.map((s) => s.id));
  const totalEnrolled = [...activeSectionIds].reduce(
    (n, id) => n + enrolledCountOf(id as string),
    0,
  );

  const fillPct =
    totalCapacity !== null ? Math.min(100, Math.round((totalEnrolled / totalCapacity) * 100)) : 0;

  // Students to show (ordered: enrolled first, then not enrolled)
  const studentRows = useMemo(() => {
    return Array.from(admittedStudentIds)
      .map((sid) => ({
        person: people.find((p) => p.id === sid),
        enrolledCount: studentEnrollMap.get(sid as string) ?? 0,
      }))
      .filter((r) => r.person)
      .sort((a, b) => b.enrolledCount - a.enrolledCount);
  }, [admittedStudentIds, people, studentEnrollMap]);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        borderColor: open ? 'rgba(99,102,241,0.30)' : 'divider',
        transition: 'border-color 0.2s',
      }}
    >
      {/* Program header */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        onClick={() => setOpen((p) => !p)}
        sx={{
          px: 2.5,
          py: 1.75,
          cursor: 'pointer',
          bgcolor: open ? 'rgba(99,102,241,0.06)' : 'action.hover',
          userSelect: 'none',
          '&:hover': { bgcolor: 'rgba(99,102,241,0.08)' },
          transition: 'background 0.15s',
        }}
      >
        <FolderSpecialRounded sx={{ fontSize: 20, color: 'primary.light', flexShrink: 0 }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {program.name}
          </Typography>

          {/* Admission → Enrollment funnel */}
          {admittedCount > 0 && (
            <Stack direction="row" alignItems="center" spacing={1} mt={0.3} flexWrap="wrap">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <PeopleOutlineRounded sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('sections.termDetail.funnel.admitted', { count: admittedCount })}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.disabled">
                →
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CheckCircleRounded sx={{ fontSize: 13, color: 'success.light' }} />
                <Typography variant="caption" sx={{ color: 'success.light' }}>
                  {t('sections.termDetail.funnel.enrolled', { count: enrolledCount })}
                </Typography>
              </Stack>
              <Chip
                label={`${conversionPct}%`}
                size="small"
                color={conversionPct >= 80 ? 'success' : conversionPct >= 50 ? 'warning' : 'error'}
                sx={{ fontSize: 10, height: 18, fontWeight: 700 }}
              />
              {totalCapacity !== null && (
                <>
                  <Typography variant="caption" color="text.disabled">
                    ·
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={fillPct}
                    sx={{
                      width: 60,
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'action.selected',
                      '& .MuiLinearProgress-bar': {
                        bgcolor:
                          fillPct >= 90
                            ? 'error.main'
                            : fillPct >= 60
                              ? 'warning.main'
                              : 'primary.main',
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('sections.termDetail.funnel.seats', {
                      enrolled: totalEnrolled,
                      capacity: totalCapacity,
                    })}
                  </Typography>
                </>
              )}
            </Stack>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <Chip
            size="small"
            icon={<MenuBookRounded sx={{ fontSize: 12 }} />}
            label={t('sections.subjectCount', { count: activeSections.length })}
            variant="outlined"
            sx={{ fontSize: 11, height: 22 }}
          />
          {open ? (
            <ExpandLessRounded sx={{ fontSize: 18, color: 'text.secondary' }} />
          ) : (
            <ExpandMoreRounded sx={{ fontSize: 18, color: 'text.secondary' }} />
          )}
        </Stack>
      </Stack>

      <Collapse in={open} unmountOnExit>
        {/* ── Sections table ── */}
        <Divider />
        <Box sx={{ px: 2.5, py: 1, bgcolor: 'rgba(0,0,0,0.15)' }}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            letterSpacing={0.8}
            sx={{ textTransform: 'uppercase' }}
          >
            Secciones / Materias
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    color: 'text.secondary',
                    fontWeight: 600,
                    fontSize: 11,
                    bgcolor: 'background.paper',
                  },
                }}
              >
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'course'}
                    direction={sortKey === 'course' ? sortDirection : 'asc'}
                    onClick={() => handleRequestSort('course')}
                  >
                    {t('sections.termDetail.columns.course')}
                  </TableSortLabel>
                </TableCell>
                {/* M1·H7b: el código de la sección y su horario, a la vista. */}
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {t('sections.termDetail.columns.code')}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  {t('sections.termDetail.columns.schedule')}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <TableSortLabel
                    active={sortKey === 'startDate'}
                    direction={sortKey === 'startDate' ? sortDirection : 'asc'}
                    onClick={() => handleRequestSort('startDate')}
                  >
                    {t('sections.termDetail.columns.start')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <TableSortLabel
                    active={sortKey === 'faculty'}
                    direction={sortKey === 'faculty' ? sortDirection : 'asc'}
                    onClick={() => handleRequestSort('faculty')}
                  >
                    {t('sections.termDetail.columns.faculty')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'enrollment'}
                    direction={sortKey === 'enrollment' ? sortDirection : 'asc'}
                    onClick={() => handleRequestSort('enrollment')}
                  >
                    {t('sections.termDetail.columns.enrollment')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === 'status'}
                    direction={sortKey === 'status' ? sortDirection : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    {t('sections.termDetail.columns.status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">{t('sections.termDetail.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSections.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    sx={{ textAlign: 'center', color: 'text.disabled', py: 3 }}
                  >
                    {t('sections.termDetail.noSections')}
                  </TableCell>
                </TableRow>
              ) : (
                sortedSections.map((s) => {
                  const course = courses.find((c) => c.id === s.courseId);
                  const facultyName = facultyNameOf(s.facultyId as string | undefined);
                  const enrolled = enrolledCountOf(s.id as string);
                  const capacity = definedCapacity(s.maxEnrollment) ?? 0;
                  const sectionFill =
                    capacity > 0 ? Math.min(100, Math.round((enrolled / capacity) * 100)) : 0;

                  return (
                    <TableRow
                      key={s.id as string}
                      hover
                      sx={{ opacity: s.status === 'cancelled' ? 0.45 : 1 }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} fontSize={13}>
                          {course?.name || s.courseId}
                        </Typography>
                        {course?.code && (
                          <Typography variant="caption" color="text.secondary">
                            {course.code}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ fontSize: 12, display: { xs: 'none', md: 'table-cell' } }}>
                        {(s.sectionCode as string | undefined) || '—'}
                      </TableCell>

                      <TableCell
                        sx={{
                          color: 'text.secondary',
                          fontSize: 12,
                          display: { xs: 'none', lg: 'table-cell' },
                        }}
                      >
                        {Array.isArray(s.schedule) && s.schedule.length > 0
                          ? (s.schedule as ScheduleBlock[]).map((b) => blockText(b)).join(' / ')
                          : t('sections.termDetail.noSchedule')}
                      </TableCell>

                      <TableCell
                        sx={{
                          color: 'text.secondary',
                          fontSize: 12,
                          display: { xs: 'none', lg: 'table-cell' },
                        }}
                      >
                        {fmtDate(s.startDate) || '—'}
                      </TableCell>

                      <TableCell
                        sx={{
                          color: 'text.secondary',
                          fontSize: 12,
                          display: { xs: 'none', md: 'table-cell' },
                        }}
                      >
                        {facultyName || '—'}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography fontSize={13}>
                            <span style={{ color: sectionFill >= 90 ? '#f87171' : '#818cf8' }}>
                              {enrolled}
                            </span>
                            {capacity > 0 && <span style={{ opacity: 0.45 }}> / {capacity}</span>}
                          </Typography>
                          {capacity > 0 && (
                            <LinearProgress
                              variant="determinate"
                              value={sectionFill}
                              sx={{
                                width: 40,
                                height: 3,
                                borderRadius: 2,
                                bgcolor: 'action.selected',
                                display: { xs: 'none', sm: 'block' },
                                '& .MuiLinearProgress-bar': {
                                  bgcolor:
                                    sectionFill >= 90
                                      ? 'error.main'
                                      : sectionFill >= 60
                                        ? 'warning.main'
                                        : 'primary.main',
                                  borderRadius: 2,
                                },
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={statusLabel(s.status as string, t)}
                          size="small"
                          color={
                            s.status === 'open'
                              ? 'success'
                              : s.status === 'cancelled'
                                ? 'error'
                                : 'default'
                          }
                          variant="outlined"
                          sx={{ fontSize: 11, height: 20 }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end" gap={0.5}>
                          {canOpenActa(s) && (
                            <Tooltip title={t('acta.open')}>
                              <IconButton
                                size="small"
                                aria-label={t('acta.open')}
                                onClick={() => navigate(`/sections/acta/${s.id as string}`)}
                              >
                                <FactCheckRounded sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canOpenRoster(s) && (
                            <Tooltip title={t('sections.roster.tooltip')}>
                              <IconButton
                                size="small"
                                aria-label={t('sections.roster.tooltip')}
                                onClick={() => setRosterSection(s)}
                              >
                                <PeopleOutlineRounded sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* H10: lo que el servidor rechaza no se muestra. Editar,
                              cancelar, reactivar, borrar, matricular, dar de baja
                              y cambiar de sección son de administración, registro
                              y decanato (`canEdit` = SECTION_MANAGEMENT_ROLES). */}
                          {sectionRowActions({
                            canManage: canEdit,
                            status: s.status as string,
                          }).includes('edit') && (
                            <Tooltip title={t('sections.termDetail.tooltips.editSection')}>
                              <IconButton size="small" onClick={() => onEdit(s)}>
                                <EditRounded sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {s.status !== 'cancelled' && (
                            <ResyncSyllabusButton
                              sectionId={s.id as string}
                              label={`${s.courseName ?? ''} ${s.sectionCode ? `(${s.sectionCode})` : ''}`.trim()}
                            />
                          )}
                          {sectionRowActions({ canManage: canEdit, status: s.status as string })
                            .length === 0 ? null : s.status === 'cancelled' ? (
                            // Sección cancelada: Reactivar + Eliminar
                            <>
                              {/* Reactivar es `updateSection` con `status:
                                  'open'`: el mismo callable, la misma guarda. */}
                              <Tooltip title={t('sections.termDetail.tooltips.reactivate')}>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => onReactivate(s)}
                                >
                                  <PlayArrowRounded sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('sections.termDetail.tooltips.deletePermanently')}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => onHardDelete(s)}
                                >
                                  <DeleteForeverRounded sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            // Sección activa: Cancelar + Matricular
                            <>
                              <Tooltip title={t('sections.termDetail.tooltips.cancelSection')}>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => onDelete(s)}
                                >
                                  <BlockRounded sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('sections.termDetail.tooltips.enrollStudent')}>
                                <Button
                                  size="small"
                                  startIcon={<PersonAddAlt1Rounded sx={{ fontSize: 13 }} />}
                                  onClick={() => onEnroll(s)}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: 12,
                                    minWidth: 0,
                                    px: 1,
                                  }}
                                >
                                  {t('sections.termDetail.enroll')}
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Students table ── */}
        {studentRows.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2.5, py: 1, bgcolor: 'rgba(0,0,0,0.15)' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => setShowStudents((p) => !p)}
                sx={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  letterSpacing={0.8}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Alumnos del programa
                </Typography>
                {showStudents ? (
                  <ExpandLessRounded sx={{ fontSize: 15, color: 'text.disabled' }} />
                ) : (
                  <ExpandMoreRounded sx={{ fontSize: 15, color: 'text.disabled' }} />
                )}
              </Stack>
            </Box>
            <Collapse in={showStudents} unmountOnExit>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          color: 'text.secondary',
                          fontWeight: 600,
                          fontSize: 11,
                          bgcolor: 'background.paper',
                        },
                      }}
                    >
                      <TableCell>Alumno</TableCell>
                      <TableCell>Estado en este período</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {studentRows.map(({ person, enrolledCount: cnt }) => (
                      <StudentStatusRow
                        key={person!.id as string}
                        person={person!}
                        enrolledSectionCount={cnt}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </>
        )}
      </Collapse>

      <Dialog
        open={rosterSection !== null}
        onClose={() => setRosterSection(null)}
        maxWidth="sm"
        fullWidth
      >
        {(() => {
          if (!rosterSection) return null;
          // Ya viene filtrada (la regla única de «inscrito») y ordenada por
          // apellido. Solo una inscripción vigente se puede retirar o mover.
          const roster = (rosterQ.data?.rows ?? []).map((r) => ({
            p: {
              id: r.studentId,
              firstName: r.firstName,
              lastName: r.lastName,
              email: r.email ?? '',
            } as DocumentData,
            enrollmentId: r.enrollmentId,
            canDrop: r.status === 'enrolled',
          }));
          const course = courses.find((c) => c.id === rosterSection.courseId);
          // Las otras secciones abiertas del MISMO curso en este período (H8).
          // Abiertas o CERRADAS a inscripciones (el cambio es del personal);
          // nunca canceladas ni con el acta cerrada. Lo exige el servidor.
          const moveOptions = allSections.filter(
            (s) =>
              s.id !== rosterSection.id &&
              s.courseId === rosterSection.courseId &&
              (s.status === 'open' || s.status === 'closed'),
          );
          const fullName = (p: DocumentData) =>
            `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || '—';
          const copyAllText = roster
            .map(({ p }) => `${fullName(p)}\t${(p.email as string) || ''}`)
            .join('\n');
          return (
            <>
              <DialogTitle sx={{ pb: 0.5 }}>
                {t('sections.roster.title')}
                <Typography variant="body2" color="text.secondary">
                  {course?.name || (rosterSection.courseId as string)} ·{' '}
                  {t('sections.roster.count', { count: roster.length })}
                </Typography>
              </DialogTitle>
              <DialogContent dividers>
                {dropNotice && (
                  <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDropNotice(null)}>
                    {dropNotice}
                  </Alert>
                )}
                {/* NUNCA VACÍA POR UN RECHAZO: si el servidor no la dio, se
                    dice por qué, no «sin alumnos». */}
                {rosterQ.isLoading ? (
                  <Stack alignItems="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : rosterQ.isError ? (
                  <Alert severity="error">{errorText(rosterQ.error)}</Alert>
                ) : roster.length === 0 ? (
                  <DialogContentText sx={{ py: 2 }}>{t('sections.roster.empty')}</DialogContentText>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{ '& th': { color: 'text.secondary', fontWeight: 600, fontSize: 11 } }}
                      >
                        <TableCell>{t('sections.roster.columns.name')}</TableCell>
                        <TableCell>{t('sections.roster.columns.email')}</TableCell>
                        <TableCell padding="checkbox" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roster.map(({ p, enrollmentId, canDrop }) => {
                        const rowKey = p.id as string;
                        const rowText = `${fullName(p)}\t${(p.email as string) || ''}`;
                        return (
                          <TableRow key={rowKey} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} fontSize={13}>
                                {fullName(p)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                              {(p.email as string) || '—'}
                            </TableCell>
                            <TableCell padding="checkbox">
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip
                                  title={
                                    copiedKey === rowKey
                                      ? t('sections.roster.copied')
                                      : t('sections.roster.copy')
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => copyRoster(rowText, rowKey)}
                                  >
                                    {copiedKey === rowKey ? (
                                      <CheckRounded sx={{ fontSize: 15, color: 'success.main' }} />
                                    ) : (
                                      <ContentCopyRounded sx={{ fontSize: 14 }} />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                {rosterRowActions({
                                  canManage: canEdit,
                                  active: canDrop,
                                  hasMoveTarget: moveOptions.length > 0,
                                }).move && (
                                  <Tooltip title={t('sections.roster.move')}>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setMoveError(null);
                                        setMoveTo('');
                                        setMoveTarget({
                                          enrollmentId,
                                          name: fullName(p),
                                          options: moveOptions,
                                        });
                                      }}
                                    >
                                      <SwapHorizRounded sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {rosterRowActions({
                                  canManage: canEdit,
                                  active: canDrop,
                                  hasMoveTarget: moveOptions.length > 0,
                                }).drop && (
                                  <Tooltip title={t('sections.roster.drop')}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={isDroppingEnrollment}
                                      onClick={() => {
                                        setDropError(null);
                                        setDropReason('');
                                        setDropTarget({
                                          enrollmentId,
                                          name: fullName(p),
                                          started: sectionStarted(
                                            rosterSection,
                                            termStart,
                                            todayYmd,
                                          ),
                                        });
                                      }}
                                    >
                                      <PersonRemoveRounded sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  disabled={roster.length === 0}
                  startIcon={
                    copiedKey === 'all' ? (
                      <CheckRounded sx={{ fontSize: 16 }} />
                    ) : (
                      <ContentCopyRounded sx={{ fontSize: 15 }} />
                    )
                  }
                  onClick={() => copyRoster(copyAllText, 'all')}
                >
                  {copiedKey === 'all' ? t('sections.roster.copied') : t('sections.roster.copyAll')}
                </Button>
                <Button onClick={() => setRosterSection(null)}>{t('sections.roster.close')}</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Confirm drop */}
      <Dialog
        open={dropTarget !== null}
        onClose={() => setDropTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('sections.roster.drop')}</DialogTitle>
        <DialogContent>
          {dropError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dropError}
            </Alert>
          )}
          <DialogContentText>
            {dropTarget
              ? dropTarget.started
                ? t('sections.roster.dropConfirmW', { name: dropTarget.name })
                : t('sections.roster.dropConfirmNoTrace', { name: dropTarget.name })
              : ''}
          </DialogContentText>
          {dropTarget?.started && financeOn && (
            <DialogContentText sx={{ mt: 1.5 }}>
              {t('sections.roster.wBillingNotice')}
            </DialogContentText>
          )}
          {dropTarget?.started && (
            <TextField
              label={t('sections.roster.dropReason')}
              value={dropReason}
              onChange={(e) => setDropReason(e.target.value)}
              fullWidth
              size="small"
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDropTarget(null)} disabled={isDroppingEnrollment}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDrop}
            disabled={isDroppingEnrollment}
          >
            {isDroppingEnrollment
              ? t('sections.roster.dropping')
              : dropTarget?.started
                ? t('sections.roster.dropActionW')
                : t('sections.roster.drop')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* H8: cambio de sección (mismo curso y período) */}
      <Dialog
        open={moveTarget !== null}
        onClose={() => setMoveTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('sections.roster.move')}</DialogTitle>
        <DialogContent>
          {moveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {moveError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            {moveTarget ? t('sections.roster.moveHelp', { name: moveTarget.name }) : ''}
          </DialogContentText>
          <TextField
            select
            fullWidth
            size="small"
            label={t('sections.roster.moveTo')}
            value={moveTo}
            onChange={(e) => setMoveTo(e.target.value)}
          >
            {(moveTarget?.options ?? []).map((s) => {
              const faculty = facultyNameOf(s.facultyId as string | undefined);
              return (
                <MenuItem key={s.id as string} value={s.id as string}>
                  {(s.sectionCode as string) || t('sections.roster.moveNoCode')}
                  {faculty ? ` · ${faculty}` : ''}
                </MenuItem>
              );
            })}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setMoveTarget(null)} disabled={moving}>
            {t('common:cancel')}
          </Button>
          <Button variant="contained" onClick={confirmMove} disabled={moving || !moveTo}>
            {moving ? t('sections.roster.moving') : t('sections.roster.moveConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ─── TermDetailPage ───────────────────────────────────────────────────────────

export function TermDetailPage() {
  const { t } = useTranslation('sis');
  const { termId } = useParams<{ termId: string }>();
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  // LOS MISMOS ROLES QUE EXIGE EL SERVIDOR. El docente y el mentor llegan a
  // esta pantalla para VER sus secciones; cambiar el titular, el programa o el
  // estado es trabajo de registro. La lista vive en el hook, no acá: estaba
  // escrita a mano y era cuestión de tiempo que se desalineara del servidor.
  const puedeEditarSecciones = useCanManageAcademics();
  const headerActions = termHeaderActions({ canManage: puedeEditarSecciones });
  // QUIÉN LEE QUÉ DIRECTO DE FIRESTORE (PR 3A). Gestión —que sigue leyendo
  // inscripciones y fichas— mantiene la vista en vivo. El docente y el mentor
  // reciben del servidor sólo cifras y nombres de docentes
  // (`getTermSectionsOverview`), y la nómina por `listSectionRoster`.
  // Las admisiones las leen administración y registro: para los demás la
  // consulta fallaba entera, y en silencio.
  const membership = useCurrentMembership();
  const leeAdmisiones =
    !!membership?.roles?.includes('tenantAdmin') || !!membership?.roles?.includes('registrar');
  /** Una lectura de la pantalla que se rechazó. Se muestra: una tabla con
   *  ceros sin explicación se lee como «no hay nadie». */
  const [loadError, setLoadError] = useState<string | null>(null);

  const [sections, setSections] = useState<DocumentData[]>([]);
  const [courses, setCourses] = useState<DocumentData[]>([]);
  const [programs, setPrograms] = useState<DocumentData[]>([]);
  const [term, setTerm] = useState<DocumentData | null>(null);
  // Personas POR ID: solo las que la pantalla nombra (docentes de las
  // secciones, alumnos inscritos, admitidos). La lista completa se carga solo
  // al abrir los diálogos que la necesitan (inscribir, editar sección).
  const [peopleById, setPeopleById] = useState<Map<string, DocumentData>>(new Map());
  const [allPeople, setAllPeople] = useState<DocumentData[] | null>(null);
  // Inscripciones de LAS SECCIONES DE ESTE PERÍODO, por lote de ids.
  const [enrollmentsByChunk, setEnrollmentsByChunk] = useState<Record<string, DocumentData[]>>({});
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const [admissions, setAdmissions] = useState<DocumentData[]>([]);

  const [editOpen, setEditOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hardDeleteOpen, setHardDeleteOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<DocumentData | null>(null);
  const [sectionToDelete, setSectionToDelete] = useState<DocumentData | null>(null);
  const [sectionToHardDelete, setSectionToHardDelete] = useState<DocumentData | null>(null);
  // Toggle: ocultar secciones canceladas por defecto
  const [showCancelled, setShowCancelled] = useState(false);
  // Editar datos del período
  const [editTermOpen, setEditTermOpen] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [genMsg, setGenMsg] = useState<string | null>(null);

  const openCatalogPrograms = useMemo(
    () => programs.filter((p) => (p['offeringMode'] as string | undefined) === 'open_catalog'),
    [programs],
  );

  const handleGenerateCatalog = async () => {
    if (!currentTenantId || !termId || openCatalogPrograms.length === 0) return;
    setGenBusy(true);
    setGenMsg(null);
    try {
      let created = 0;
      let skipped = 0;
      for (const p of openCatalogPrograms) {
        const r = await academicApi.generateTermSections({
          tenantId: currentTenantId,
          programId: p.id as string,
          termId,
        });
        created += r.created;
        skipped += r.skipped;
      }
      setGenMsg(t('sections.generateCatalog.result', { created, skipped }));
    } catch (e) {
      setGenMsg((e as Error).message);
    } finally {
      setGenBusy(false);
    }
  };

  const {
    deleteSection,
    isDeletingSection,
    hardDeleteSection,
    isHardDeletingSection,
    updateSection,
  } = useAcademic();

  // Helper: cuántas matrículas activas tiene una sección
  const sectionEnrollmentCount = (sectionId: string) =>
    enrollments.filter((e) => e.sectionId === sectionId).length;

  // ANTES esta pantalla escuchaba TODAS las inscripciones del seminario, todos
  // los períodos y todas las personas: en un seminario con historia importada
  // eran ~6.000 lecturas por apertura, y cualquier inscripción en cualquier
  // período reenviaba la colección entera. El total «Matriculados» contaba
  // los alumnos de toda la historia, igual en todos los períodos.
  useEffect(() => {
    if (!currentTenantId || !termId) return;
    setSectionsLoaded(false);

    setLoadError(null);
    const onError = (e: unknown) => setLoadError(errorText(e));

    const unsubTerm = onSnapshot(
      doc(db, `tenants/${currentTenantId}/terms/${termId}`),
      (d) => {
        setTerm(d.exists() ? { id: d.id, ...d.data() } : null);
      },
      onError,
    );

    const unsubSections = onSnapshot(
      query(collection(db, `tenants/${currentTenantId}/sections`), where('termId', '==', termId)),
      (s) => {
        setSections(s.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSectionsLoaded(true);
      },
      (e) => {
        onError(e);
        // Sin esto la pantalla se quedaba en la rueda para siempre.
        setSectionsLoaded(true);
      },
    );
    const unsubCourses = onSnapshot(
      collection(db, `tenants/${currentTenantId}/courses`),
      (s) => setCourses(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );
    const unsubPrograms = onSnapshot(
      collection(db, `tenants/${currentTenantId}/programs`),
      (s) =>
        setPrograms(
          (s.docs.map((d) => ({ id: d.id, ...d.data() })) as any[]).filter((p) => !p.deletedAt),
        ),
      onError,
    );
    // Load accepted admissions only — sólo quien puede leerlas.
    const unsubAdmissions = leeAdmisiones
      ? onSnapshot(
          query(
            collection(db, `tenants/${currentTenantId}/admissions`),
            where('status', '==', 'accepted'),
          ),
          (s) => setAdmissions(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
          onError,
        )
      : () => undefined;
    if (!leeAdmisiones) setAdmissions([]);

    return () => {
      unsubTerm();
      unsubSections();
      unsubCourses();
      unsubPrograms();
      unsubAdmissions();
    };
  }, [currentTenantId, termId, leeAdmisiones]);

  // Inscripciones de las secciones del período, en vivo, de a 30 ids (el tope
  // de `in`). SÓLO GESTIÓN (ver arriba). Se mira por sección y no por `termId` del documento: es lo que
  // la pantalla agrupa.
  const sectionIdsKey = useMemo(
    () =>
      sections
        .map((s) => s.id as string)
        .sort()
        .join(','),
    [sections],
  );
  useEffect(() => {
    setEnrollmentsByChunk({});
    if (!currentTenantId || !sectionIdsKey || !puedeEditarSecciones) return;
    const ids = sectionIdsKey.split(',');
    const unsubs: Array<() => void> = [];
    for (let i = 0; i < ids.length; i += 30) {
      const chunk = ids.slice(i, i + 30);
      const key = String(i);
      unsubs.push(
        onSnapshot(
          query(
            collection(db, `tenants/${currentTenantId}/enrollments`),
            where('sectionId', 'in', chunk),
          ),
          (s) =>
            setEnrollmentsByChunk((prev) => ({
              ...prev,
              [key]: s.docs.map((d) => ({ id: d.id, ...d.data() })),
            })),
          (e) => setLoadError(errorText(e)),
        ),
      );
    }
    return () => unsubs.forEach((u) => u());
  }, [currentTenantId, sectionIdsKey, puedeEditarSecciones]);
  const enrollments = useMemo(() => Object.values(enrollmentsByChunk).flat(), [enrollmentsByChunk]);

  // Las personas que la pantalla nombra, por id (de a 30).
  const neededPersonIdsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const s of sections) if (s.facultyId) ids.add(s.facultyId as string);
    for (const e of enrollments) if (e.studentId) ids.add(e.studentId as string);
    for (const a of admissions) {
      const id = (a.personId ?? a.studentId) as string | undefined;
      if (id) ids.add(id);
    }
    return [...ids].sort().join(',');
  }, [sections, enrollments, admissions]);
  // Cada id se pide UNA vez: las inscripciones llegan por lotes y la clave
  // cambia varias veces durante la carga.
  const requestedPersonIds = useRef(new Set<string>());
  useEffect(() => {
    requestedPersonIds.current = new Set();
    setPeopleById(new Map());
    setAllPeople(null);
  }, [currentTenantId]);
  useEffect(() => {
    // Las fichas por id son de gestión: a los demás el servidor les da los
    // nombres de los docentes (ver `overviewQ`).
    if (!currentTenantId || !neededPersonIdsKey || !puedeEditarSecciones) return;
    const missing = neededPersonIdsKey
      .split(',')
      .filter((id) => !requestedPersonIds.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => requestedPersonIds.current.add(id));
    (async () => {
      for (let i = 0; i < missing.length; i += 30) {
        const snap = await getDocs(
          query(
            collection(db, `tenants/${currentTenantId}/people`),
            where(documentId(), 'in', missing.slice(i, i + 30)),
          ),
        );
        const found = snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }] as const);
        setPeopleById((prev) => new Map([...prev, ...found]));
      }
    })().catch(() => {
      /* sin nombres: la pantalla muestra «—» */
    });
  }, [currentTenantId, neededPersonIdsKey, puedeEditarSecciones]);

  // LO QUE EL DOCENTE Y EL MENTOR SABEN DEL PERÍODO, del servidor: el titular
  // y los inscritos de cada sección, y los alumnos distintos. Sin alumnos.
  const overviewQ = useQuery({
    queryKey: ['term-sections-overview', currentTenantId, termId],
    enabled: !!currentTenantId && !!termId && !puedeEditarSecciones,
    queryFn: () =>
      academicApi.getTermSectionsOverview({ tenantId: currentTenantId!, termId: termId! }),
    staleTime: 60_000,
  });
  const overviewById = useMemo(
    () => new Map((overviewQ.data?.sections ?? []).map((r) => [r.sectionId, r])),
    [overviewQ.data],
  );

  // La lista completa (alumnos para inscribir, docentes para editar), solo
  // cuando se abre un diálogo que la usa.
  useEffect(() => {
    if (!currentTenantId || allPeople || !(enrollOpen || editOpen)) return;
    getDocs(
      query(collection(db, `tenants/${currentTenantId}/people`), where('deletedAt', '==', null)),
    )
      .then((s) => setAllPeople(s.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(() => setAllPeople([]));
  }, [currentTenantId, allPeople, enrollOpen, editOpen]);

  const people = useMemo(() => {
    const byId = new Map(peopleById);
    for (const p of allPeople ?? []) byId.set(p.id as string, p);
    return [...byId.values()].filter((p) => !p.deletedAt);
  }, [peopleById, allPeople]);
  const loading = !sectionsLoaded;

  // DE DÓNDE SALE CADA CIFRA. Gestión cuenta sobre sus inscripciones en vivo;
  // los demás leen el resumen del servidor. Las dos con la misma regla de
  // «inscrito» (countsAsEnrolled, del dominio).
  const enrolledCountOf = useCallback(
    (sectionId: string) =>
      puedeEditarSecciones
        ? enrollments.filter((e) => e.sectionId === sectionId && countsAsEnrolled(e)).length
        : (overviewById.get(sectionId)?.enrolledCount ?? 0),
    [puedeEditarSecciones, enrollments, overviewById],
  );
  const facultyNameBySection = useMemo(() => {
    const m = new Map<string, string>();
    for (const sec of sections) {
      const fid = sec.facultyId as string | undefined;
      const name = overviewById.get(sec.id as string)?.facultyName;
      if (fid && name) m.set(fid, name);
    }
    return m;
  }, [sections, overviewById]);
  const facultyNameOf = useCallback(
    (facultyId: string | undefined) => {
      if (!facultyId) return '';
      if (!puedeEditarSecciones) return facultyNameBySection.get(facultyId) ?? '';
      const p = people.find((pp) => pp.id === facultyId);
      return p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : '';
    },
    [puedeEditarSecciones, facultyNameBySection, people],
  );

  const facultyPeople = useMemo(
    () =>
      (allPeople ?? []).filter(
        (p) =>
          p.types?.some((t: string) => FACULTY_ROLES.includes(t)) ||
          p.institutionalRoles?.some((r: string) => FACULTY_ROLES.includes(r)),
      ),
    [allPeople],
  );

  // Group sections by program using section.programId (catalog refactor: programId lives on section, not course)
  const programGroups = useMemo(() => {
    const map = new Map<
      string,
      { program: { id: string; name: string }; sections: DocumentData[] }
    >();

    // Filtrar secciones canceladas si el toggle está desactivado
    const visibleSections = showCancelled
      ? sections
      : sections.filter((s) => s.status !== 'cancelled');

    for (const sec of visibleSections) {
      // After the catalog refactor, programId is stored directly on the section document.
      // Legacy sections (pre-migration) will have an empty string; show them under 'Sin programa'.
      const programId: string = sec.programId || '__none__';
      if (!map.has(programId)) {
        const prog = programs.find((p) => p.id === programId);
        map.set(programId, {
          program: { id: programId, name: prog?.name ?? 'Sin programa' },
          sections: [],
        });
      }
      map.get(programId)!.sections.push(sec);
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.program.id === '__none__') return 1;
      if (b.program.id === '__none__') return -1;
      return a.program.name.localeCompare(b.program.name);
    });
  }, [sections, programs, showCancelled]);

  // Cuenta de secciones canceladas (para el badge del toggle)
  const cancelledCount = useMemo(
    () => sections.filter((s) => s.status === 'cancelled').length,
    [sections],
  );

  // Global metrics
  const totalAdmitted = useMemo(() => {
    const programIds = new Set(programs.map((p) => p.id));
    return new Set(
      admissions
        .filter((a) => programIds.has(a.programId))
        .map((a) => a.personId ?? a.studentId)
        .filter(Boolean),
    ).size;
  }, [admissions, programs]);
  const totalEnrolledStudents = useMemo(() => {
    if (!puedeEditarSecciones) return overviewQ.data?.studentCount ?? 0;
    return new Set(enrollments.map((e) => e.studentId).filter(Boolean)).size;
  }, [puedeEditarSecciones, overviewQ.data, enrollments]);

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Stack>
    );
  }

  if (!term) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.disabled">{t('sections.termDetail.notFound')}</Typography>
        <Button
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate('/sections')}
          sx={{ mt: 2 }}
        >
          {t('sections.termDetail.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Lo que no se pudo leer se dice: sin esto, ceros y guiones que
          parecen datos (PR 3A, «ninguna pantalla falla en silencio»). */}
      {(loadError || overviewQ.isError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError ?? errorText(overviewQ.error)}
        </Alert>
      )}

      {/* ── Header ── */}
      <Stack direction="row" alignItems="flex-start" spacing={2} mb={3} flexWrap="wrap">
        <Button
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate('/sections')}
          sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, mt: 0.5 }}
        >
          Períodos
        </Button>

        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <Typography variant="h5" fontWeight={700} color="white">
              {term.name as string}
            </Typography>
            {term.status && (
              <Chip
                label={statusLabel(term.status as string, t)}
                size="small"
                color={statusColor(term.status as string)}
                sx={{ fontWeight: 600, fontSize: 12 }}
              />
            )}
          </Stack>

          {/* Dates + global stats */}
          <Stack direction="row" spacing={2} mt={0.75} flexWrap="wrap">
            {(term.startDate || term.endDate) && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CalendarMonthRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                  {fmtDate(term.startDate) || '?'} – {fmtDate(term.endDate) || '?'}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} flexShrink={0}>
          {openCatalogPrograms.length > 0 && puedeEditarSecciones && (
            <Button
              variant="outlined"
              color="secondary"
              disabled={genBusy}
              startIcon={
                genBusy ? <CircularProgress size={14} /> : <MenuBookRounded sx={{ fontSize: 16 }} />
              }
              onClick={handleGenerateCatalog}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('sections.generateCatalog.button')}
            </Button>
          )}
          {/* Gestión del período: solo para quien gestiona (2a-1b). El servidor
              ya rechazaba ambas acciones al docente y al mentor. */}
          {headerActions.includes('editTerm') && (
            <Button
              variant="outlined"
              startIcon={<EditRounded sx={{ fontSize: 16 }} />}
              onClick={() => setEditTermOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('sections.termDetail.editTerm')}
            </Button>
          )}
          {headerActions.includes('configureSubjects') && (
            <Button
              variant="outlined"
              startIcon={<TuneRounded sx={{ fontSize: 16 }} />}
              onClick={() => navigate(`/sections/configure/${termId}`)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('sections.termDetail.configureSubjects')}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* ── Global KPI cards ── */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        {/* ── Programas activos ── */}
        <Paper
          variant="outlined"
          sx={{
            px: 2.5,
            py: 1.75,
            borderRadius: 2,
            flex: '1 1 120px',
            borderColor: 'rgba(255,255,255,0.08)',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        >
          <Typography variant="h5" fontWeight={800} sx={{ color: '#818cf8' }}>
            {programGroups.filter((g) => g.program.id !== '__none__').length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('sections.termDetail.metrics.programs')}
          </Typography>
          <Typography
            variant="caption"
            display="block"
            color="text.disabled"
            sx={{ fontSize: 10, mt: 0.25 }}
          >
            {t('sections.termDetail.metrics.inThisTerm')}
          </Typography>
        </Paper>

        {/* ── Secciones ── */}
        <Paper
          variant="outlined"
          sx={{
            px: 2.5,
            py: 1.75,
            borderRadius: 2,
            flex: '1 1 120px',
            borderColor: 'rgba(255,255,255,0.08)',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        >
          <Typography variant="h5" fontWeight={800} sx={{ color: '#a78bfa' }}>
            {sections.filter((s) => s.status !== 'cancelled').length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('sections.termDetail.metrics.sections')}
          </Typography>
        </Paper>

        {/* ── Admitidos (personas únicas) ── */}
        <Paper
          variant="outlined"
          sx={{
            px: 2.5,
            py: 1.75,
            borderRadius: 2,
            flex: '1 1 120px',
            borderColor: 'rgba(255,255,255,0.08)',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        >
          <Typography variant="h5" fontWeight={800} sx={{ color: '#60a5fa' }}>
            {totalAdmitted}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('sections.termDetail.metrics.admitted')}
          </Typography>
          <Typography
            variant="caption"
            display="block"
            color="text.disabled"
            sx={{ fontSize: 10, mt: 0.25 }}
          >
            {t('sections.termDetail.metrics.admittedHelp')}
          </Typography>
        </Paper>

        {/* ── Matriculados ── */}
        <Paper
          variant="outlined"
          sx={{
            px: 2.5,
            py: 1.75,
            borderRadius: 2,
            flex: '1 1 120px',
            borderColor: 'rgba(255,255,255,0.08)',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        >
          <Typography variant="h5" fontWeight={800} sx={{ color: '#34d399' }}>
            {totalEnrolledStudents}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('sections.termDetail.metrics.enrolled')}
          </Typography>
          <Typography
            variant="caption"
            display="block"
            color="text.disabled"
            sx={{ fontSize: 10, mt: 0.25 }}
          >
            {t('sections.termDetail.metrics.enrolledHelp')}
          </Typography>
        </Paper>

        {/* ── Distribución por programa ── */}
        {totalEnrolledStudents > 0 && (
          <Paper
            variant="outlined"
            sx={{
              px: 2.5,
              py: 1.75,
              borderRadius: 2,
              flex: '2 1 200px',
              borderColor: 'rgba(255,255,255,0.08)',
              bgcolor: 'rgba(255,255,255,0.03)',
            }}
          >
            {/* Métrica: % de alumnos del seminario inscritos en cada programa */}
            {(() => {
              // Total de alumnos únicos en el seminario = personas con ≥1 admisión accepted
              const totalSeminaryStudents = new Set(
                admissions.map((a) => a.personId ?? a.studentId).filter(Boolean),
              ).size;

              // Por programa (todos los programas activos, no solo los del período)
              const programAdmissions = programs
                .filter((p) => p.status === 'active' || !p.status)
                .map((p) => ({
                  name: p.name as string,
                  id: p.id as string,
                  count: new Set(
                    admissions
                      .filter((a) => a.programId === p.id)
                      .map((a) => a.personId ?? a.studentId)
                      .filter(Boolean),
                  ).size,
                }))
                .filter((p) => p.count > 0);

              if (totalSeminaryStudents === 0 || programAdmissions.length === 0) return null;

              const barColors = ['#60a5fa', '#34d399', '#f472b6', '#fb923c', '#a78bfa'];

              return (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                    {t('sections.termDetail.coverage.title')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    display="block"
                    sx={{ fontSize: 10, mb: 1 }}
                  >
                    {t('sections.termDetail.coverage.help', { count: totalSeminaryStudents })}
                  </Typography>
                  <Stack spacing={0.6}>
                    {programAdmissions.map((p, idx) => {
                      const color = barColors[idx % barColors.length];
                      const pct = Math.round((p.count / totalSeminaryStudents) * 100);
                      return (
                        <Stack key={p.id} direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              minWidth: 110,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: 10,
                            }}
                          >
                            {p.name}
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              height: 5,
                              borderRadius: 99,
                              bgcolor: 'rgba(255,255,255,0.07)',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${pct}%`,
                                height: '100%',
                                bgcolor: color,
                                borderRadius: 99,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color,
                              fontWeight: 700,
                              fontSize: 10,
                              minWidth: 60,
                              textAlign: 'right',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {p.count}/{totalSeminaryStudents} · {pct}%
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </>
              );
            })()}
          </Paper>
        )}
      </Stack>

      {/* ── Toggle secciones canceladas ── */}
      {cancelledCount > 0 && (
        <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={1}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Mostrar canceladas ({cancelledCount})
              </Typography>
            }
            labelPlacement="start"
          />
        </Stack>
      )}

      {/* ── Program panels ── */}
      {programGroups.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 8, textAlign: 'center' }}>
          <Typography color="text.disabled">
            {cancelledCount > 0 && !showCancelled
              ? `Todas las secciones están canceladas. Activa "Mostrar canceladas" para verlas.`
              : 'No hay secciones configuradas para este período.'}
          </Typography>
          {!(cancelledCount > 0 && !showCancelled) && (
            <Button
              variant="outlined"
              startIcon={<TuneRounded />}
              onClick={() => navigate(`/sections/configure/${termId}`)}
              sx={{ mt: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Configurar período
            </Button>
          )}
        </Paper>
      ) : (
        <Stack spacing={2}>
          {programGroups.map((group) => (
            <ProgramPanel
              key={group.program.id}
              program={group.program}
              sections={group.sections}
              allSections={sections}
              courses={courses}
              people={people}
              admissions={admissions}
              enrollments={enrollments}
              enrolledCountOf={enrolledCountOf}
              facultyNameOf={facultyNameOf}
              termStart={term?.startDate}
              canEdit={puedeEditarSecciones}
              onEdit={(s) => {
                setSelectedSection(s);
                setEditOpen(true);
              }}
              onDelete={(s) => {
                setSectionToDelete(s);
                setDeleteOpen(true);
              }}
              onHardDelete={(s) => {
                setSectionToHardDelete(s);
                setHardDeleteOpen(true);
              }}
              onReactivate={async (s) => {
                if (!currentTenantId) return;
                await updateSection({
                  tenantId: currentTenantId,
                  sectionId: s.id as string,
                  status: 'open',
                });
              }}
              onEnroll={(s) => {
                setSelectedSection(s);
                setEnrollOpen(true);
              }}
            />
          ))}
        </Stack>
      )}

      {/* ── Dialogs ── */}

      <EnrollStudentDialog
        open={enrollOpen}
        onClose={() => {
          setEnrollOpen(false);
          setSelectedSection(null);
        }}
        section={selectedSection}
        students={(allPeople ?? []).filter((p) => {
          if (!p.types?.includes('student')) return false;
          if (selectedSection) {
            // Exclude only students CURRENTLY enrolled — a dropped enrollment
            // leaves a 'dropped' record, which must not block re-enrollment.
            return !enrollments.some(
              (e) =>
                e.sectionId === selectedSection.id &&
                e.studentId === p.id &&
                e.status === 'enrolled',
            );
          }
          return true;
        })}
      />

      <EditSectionDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelectedSection(null);
        }}
        section={selectedSection}
        courses={courses}
        terms={term ? [term] : []}
        faculty={facultyPeople}
      />

      {/* ── Dialog: Cancelar sección (soft delete) ── */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar Sección</DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              mb:
                sectionToDelete && sectionEnrollmentCount(sectionToDelete.id as string) > 0
                  ? 1.5
                  : 0,
            }}
          >
            ¿Cancelar la sección de{' '}
            <strong>
              {courses.find((c) => c.id === sectionToDelete?.courseId)?.name ??
                sectionToDelete?.courseId}
            </strong>
            ? La sección quedará registrada con estado <em>cancelada</em> y podrás reactivarla si es
            necesario.
          </DialogContentText>
          {sectionToDelete && sectionEnrollmentCount(sectionToDelete.id as string) > 0 && (
            <Alert severity="warning" icon={<WarningAmberRounded />}>
              Esta sección tiene{' '}
              <strong>
                {sectionEnrollmentCount(sectionToDelete.id as string)} alumno(s) inscrito(s)
              </strong>
              . Al cancelarla quedarán sin asignación activa en este período.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={isDeletingSection}>
            Volver
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={isDeletingSection}
            startIcon={<BlockRounded />}
            onClick={async () => {
              if (!sectionToDelete || !currentTenantId) return;
              await deleteSection({
                tenantId: currentTenantId,
                sectionId: sectionToDelete.id as string,
              });
              setDeleteOpen(false);
              setSectionToDelete(null);
            }}
          >
            {isDeletingSection ? 'Cancelando...' : 'Cancelar sección'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Eliminar permanentemente (hard delete) ── */}
      <Dialog
        open={hardDeleteOpen}
        onClose={() => setHardDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>Eliminar Permanentemente</DialogTitle>
        <DialogContent>
          {sectionToHardDelete && sectionEnrollmentCount(sectionToHardDelete.id as string) > 0 ? (
            // Bloqueado: hay matrículas activas
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>
                  No es posible eliminar una sección con{' '}
                  {sectionEnrollmentCount(sectionToHardDelete.id as string)} alumno(s) inscrito(s).
                </strong>
              </Alert>
              <DialogContentText>
                Cancela la sección primero para retirar a los alumnos, luego podrás eliminarla
                permanentemente.
              </DialogContentText>
            </>
          ) : (
            // Permitido: sin matrículas
            <DialogContentText>
              Esta acción <strong>no se puede deshacer</strong>. Se eliminará la sección{' '}
              <strong>
                {courses.find((c) => c.id === sectionToHardDelete?.courseId)?.name ??
                  sectionToHardDelete?.courseId}
              </strong>{' '}
              y todos sus datos.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setHardDeleteOpen(false)}>Cerrar</Button>
          {sectionToHardDelete &&
            sectionEnrollmentCount(sectionToHardDelete.id as string) === 0 && (
              <Button
                variant="contained"
                color="error"
                disabled={isHardDeletingSection}
                startIcon={<DeleteForeverRounded />}
                onClick={async () => {
                  if (!sectionToHardDelete || !currentTenantId) return;
                  await hardDeleteSection({
                    tenantId: currentTenantId,
                    sectionId: sectionToHardDelete.id as string,
                  });
                  setHardDeleteOpen(false);
                  setSectionToHardDelete(null);
                }}
              >
                {isHardDeletingSection ? 'Eliminando...' : 'Eliminar permanentemente'}
              </Button>
            )}
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Editar período ── */}
      <EditTermDialog open={editTermOpen} onClose={() => setEditTermOpen(false)} term={term} />

      <Snackbar
        open={!!genMsg}
        autoHideDuration={6000}
        onClose={() => setGenMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setGenMsg(null)}>
          {genMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
